import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { pagedQuery, pagedResponseWith, PickerIdsSchema } from './pagination.js';
import { IsoDateSchema, MessageSchema, MoneyStringSchema, } from './primitives.js';
const c = initContract();
/**
 * Projects (PART 13, PRJ-01 + the PRJ-04 billing configuration).
 *
 * The module rule (§13.1.6, owner ruling R1): ONE gate — the `projects`
 * permission area. What was the prototype's Easy/Advanced second gate is
 * gone; everything the screen shows is shown to anyone who may open it.
 *
 * Derivations the SERVER answers, never stores (the module's two headline
 * defects, fixed):
 *   BR-PRJ-01-04  Spent = invoiced amounts + value of approved unbilled
 *                 hours — the SAME figure Project Billing calls consumed
 *                 (Q-PRJ-01-1: "one derived figure … shown everywhere").
 *   BR-PRJ-01-05  Progress = done tasks ÷ all tasks (Q-PRJ-01-2), from the
 *                 shared task table.
 *   BR-PRJ-01-02  Headline figures count Planning + Active + On hold only.
 *
 * BR-PRJ-01-10: the client POINTS AT A CUSTOMER RECORD; an internal project
 * has none and is non-billable automatically (§13.1.4 — nobody has to
 * remember). Q-PRJ-01-3: the project takes the customer's currency.
 */
// ── Vocabularies ────────────────────────────────────────────────────────────
export const PROJECT_STATUSES = ['planning', 'active', 'on_hold', 'completed', 'cancelled'];
export const BILLING_TYPES = ['time_materials', 'fixed_price', 'milestone', 'retainer'];
export const BILLING_FREQUENCIES = ['weekly', 'monthly', 'on_milestone', 'on_demand'];
export const MILESTONE_STATUSES = ['not_started', 'in_progress', 'completed', 'invoiced'];
/** PRJ-01 §7: which statuses the headline figures count (BR-PRJ-01-02). */
export const ACTIVE_PROJECT_STATUSES = ['planning', 'active', 'on_hold'];
/** PRJ-01 §7's intended transition model (nothing in the prototype moved one). */
export const PROJECT_STATUS_TRANSITIONS = {
    planning: ['active', 'cancelled'],
    active: ['on_hold', 'completed', 'cancelled'],
    on_hold: ['active', 'completed', 'cancelled'],
    completed: [],
    cancelled: [],
};
/** Q-PRJ-04-2 / PRJ-04 §5c: the fallback price band where none is set. */
export const DEFAULT_RATE_ROLE = 'Senior Consultant';
/** The three default rate-card rows a new project is seeded with (PRJ-04 §5a). */
export const DEFAULT_RATE_CARD = [
    { roleName: 'Project Manager', hourlyRate: '130.00' },
    { roleName: 'Senior Consultant', hourlyRate: '150.00' },
    { roleName: 'Junior Consultant', hourlyRate: '80.00' },
];
// ── The fixed sentences ─────────────────────────────────────────────────────
export const PROJECT_MESSAGES = {
    /** BR-PRJ-01-01's floor. */
    incomplete: 'A project needs a name, a client, a manager, a start date, an end date and a budget.',
    /** BR-PRJ-01-10 + the internal case. */
    clientRequired: 'The client must be a customer record, or the project must be marked internal.',
    notCustomer: (name) => `${name} is not a customer and cannot be a project's client.`,
    /** §13.1.4: internal ⇒ non-billable, automatically. */
    internalNonBillable: 'An internal project is non-billable — every hour on it follows.',
    badTransition: (from, to) => `A ${from.replace('_', ' ')} project cannot move to ${to.replace('_', ' ')}.`,
    /** BR-PRJ-01-09. */
    hasRecords: 'This project has tasks, timesheet entries or invoices and cannot be deleted. Cancel it instead.',
    memberExists: 'This person is already on the team.',
    /**
     * PRJ-01 field 4 / §11: managers and team members are EMPLOYEE records. A
     * terminated employee stays on the projects they already ran — history is
     * not rewritten — but cannot be picked onto a new one.
     */
    notAnEmployee: 'Pick somebody from the staff list. Managers and team members are employee records.',
    employeeTerminated: (name) => `${name} has left and cannot be assigned to a project.`,
    rateRoleUnknown: (role) => `${role} is not on this project's rate table.`,
    rateRoleInUse: (role) => `${role} is held by a team member and cannot be removed from the rate table.`,
    milestoneInvoiced: 'This milestone is invoiced and locked.',
};
/**
 * D46 — the project family's structured refusals (the d46po wave): key +
 * params beside the composed English sentence, so a refusal that names a
 * partner, an employee, a rate-table role or a status move reaches an Arabic
 * reader in Arabic. One builder per parameterised member the API raises
 * inside an HTTP exception body — the raise sites throw THESE, and each
 * `message` is composed by the `PROJECT_MESSAGES` twin so the two cannot
 * drift (parity pinned member by member in
 * `apps/api/test/refusal-census.spec.ts`; both-language renders in
 * `apps/web/src/test/refusal-wire-render.test.ts`).
 *
 * `badTransition`'s two params are TOKENS, not prose: the wire carries the
 * raw `PROJECT_STATUSES` values (`planning`, `on_hold`, …) and each
 * dictionary decides how to SAY them — en selects the same words the twin
 * has always composed (its only transformation is swapping the underscore
 * for a space, so the composed sentence is byte-identical for every pair
 * the raise site can pass), ar says them in Arabic. Every other param is a
 * display value — a partner's name, an employee's full name, a rate-table
 * role — embedded verbatim in both languages. No member carries a count:
 * this family pluralises nothing.
 */
export const PROJECT_REFUSALS = {
    notCustomer: (name) => ({
        message: PROJECT_MESSAGES.notCustomer(name),
        key: 'prj.errNotCustomer',
        params: { name },
    }),
    badTransition: (from, to) => ({
        message: PROJECT_MESSAGES.badTransition(from, to),
        key: 'prj.errBadTransition',
        params: { from, to },
    }),
    employeeTerminated: (name) => ({
        message: PROJECT_MESSAGES.employeeTerminated(name),
        key: 'prj.errEmployeeTerminated',
        params: { name },
    }),
    rateRoleUnknown: (role) => ({
        message: PROJECT_MESSAGES.rateRoleUnknown(role),
        key: 'prj.errRateRoleUnknown',
        params: { role },
    }),
    rateRoleInUse: (role) => ({
        message: PROJECT_MESSAGES.rateRoleInUse(role),
        key: 'prj.errRateRoleInUse',
        params: { role },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const ProjectStatusSchema = z.enum(PROJECT_STATUSES);
export const BillingTypeSchema = z.enum(BILLING_TYPES);
export const BillingFrequencySchema = z.enum(BILLING_FREQUENCIES);
export const MilestoneStatusSchema = z.enum(MILESTONE_STATUSES);
export const ProjectMemberSchema = z
    .object({
    id: z.uuid(),
    /** PRJ-01 §11: a team member is an EMPLOYEE record (HR-01). */
    employeeId: z.uuid(),
    employeeName: z.string().nullable(),
    /** §13.1.3's third meaning of "role": a price band, nothing else. */
    rateRole: z.string(),
    /**
     * PRJ-01 (installation companies): the person's JOB on this team — "site
     * lead", "electrician", "driver" — in the company's own words. Not the
     * price band above; a foreman may bill at any rate.
     */
    role: z.string().nullable(),
})
    .strict();
/**
 * One team member as the create/update forms send the WHOLE team: replace-all,
 * keyed on the employee. `rateRole` defaults to DEFAULT_RATE_ROLE and must be
 * on the project's rate table; `role` is the free-text job on site.
 */
export const TeamMemberInputSchema = z
    .object({
    employeeId: z.uuid(),
    role: z.string().trim().min(1).max(80).optional(),
    rateRole: z.string().trim().min(1).max(60).optional(),
})
    .strict();
/** PRJ-01/G-03: "lat,lng" in decimal degrees — the shape the DB CHECK pins. */
const SiteGeoSchema = z
    .string()
    .trim()
    .max(60)
    .regex(/^-?\d+(\.\d+)?,-?\d+(\.\d+)?$/);
export const RateCardRowSchema = z
    .object({
    id: z.uuid(),
    roleName: z.string(),
    hourlyRate: MoneyStringSchema,
})
    .strict();
export const MilestoneSchema = z
    .object({
    id: z.uuid(),
    name: z.string(),
    dueDate: IsoDateSchema,
    amount: MoneyStringSchema,
    status: MilestoneStatusSchema,
    /** BR-PRJ-04-09: which invoice claimed it (INV-####), once invoiced. */
    invoiceId: z.uuid().nullable(),
    invoiceCode: z.string().nullable(),
    /** PRJ-01's "done marker": completed or invoiced. */
    done: z.boolean(),
})
    .strict();
export const ProjectSchema = z
    .object({
    id: z.uuid(),
    /** PRJ-####. */
    code: z.string(),
    name: z.string(),
    /** NULL = internal (BR-PRJ-01-10). */
    partnerId: z.uuid().nullable(),
    clientName: z.string(),
    isInternal: z.boolean(),
    /** PRJ-01 field 4: an EMPLOYEE id (HR-01), never an account. */
    managerId: z.uuid(),
    managerName: z.string().nullable(),
    status: ProjectStatusSchema,
    startDate: IsoDateSchema,
    endDate: IsoDateSchema,
    /** PRJ-01 field 8 = PRJ-04 contract value. */
    budget: MoneyStringSchema,
    currency: z.string(),
    billable: z.boolean(),
    billingType: BillingTypeSchema,
    billingFrequency: BillingFrequencySchema,
    flatRate: MoneyStringSchema,
    /** BR-PRJ-01-04: DERIVED — billed + approved unbilled value. */
    spent: MoneyStringSchema,
    /** budget − spent (5a), never negative on the wire; over is flagged. */
    remaining: MoneyStringSchema,
    overBudget: z.boolean(),
    /** BR-PRJ-01-05: DERIVED — done tasks ÷ all tasks, whole percent. */
    progressPct: z.number().int().min(0).max(100),
    taskCount: z.number().int().min(0),
    doneTaskCount: z.number().int().min(0),
    notes: z.string().nullable(),
    /**
     * PRJ-01/G-03 — the installation SITE: street address, city, and an
     * optional "lat,lng" pin. All null on a project that happens nowhere in
     * particular (consultancy, internal).
     */
    siteAddress: z.string().nullable(),
    siteCity: z.string().nullable(),
    siteGeo: z.string().nullable(),
    createdAt: z.string(),
})
    .strict();
/**
 * PRJ-01 — one posted Stock OUT voucher issued to the project (INV-08's
 * `project_issue`). `cost` is Σ of the movement values the posting wrote, in
 * the COMPANY's currency (stock is valued in base — GEN-107), which is why
 * `currency` rides beside it rather than being the project's.
 */
export const ProjectMaterialIssueSchema = z
    .object({
    adjustmentId: z.uuid(),
    /** SO-####. */
    code: z.string(),
    adjustmentDate: IsoDateSchema,
    cost: MoneyStringSchema,
    currency: z.string(),
})
    .strict();
export const ProjectDetailSchema = ProjectSchema.extend({
    members: ProjectMemberSchema.array(),
    rateCard: RateCardRowSchema.array(),
    milestones: MilestoneSchema.array(),
    /** PRJ-01: posted material issues, newest first. */
    materials: ProjectMaterialIssueSchema.array(),
    /**
     * Σ `materials`, stated in the PROJECT's currency — the slice of `spent`
     * that is stock rather than hours or invoices (BR-PRJ-01-04). A foreign
     * currency project converts each issue at the rate effective on its
     * voucher date; an issue with no rate is listed but cannot be counted.
     */
    materialsCost: MoneyStringSchema,
}).strict();
/**
 * PRJ-01 §5a: the four headline figures over Planning/Active/On hold.
 *
 * CURRENCY. `activeBudget` and `spent` FOLD ACROSS PROJECTS, and a project
 * "carries its own currency code" (GEN-108) taken from its customer, so this
 * is a genuinely multi-currency total and the only one in PART 13 — every
 * other figure the projects module produces stays inside one project, where
 * one currency is the construction.
 *
 * It converts at ONE RATE PER CURRENCY as of the company's today, which is
 * DSH-04's reading and not the books' one, because a project has no frozen
 * rate to use: `projects` carries no `exchange_rate` column and never will —
 * a rate is frozen when a document is CONFIRMED (GEN-111, NFR-30) and a
 * project is never confirmed, it runs. Its budget is a forward-looking
 * figure about money not yet spent, so "what are the running projects worth
 * today" is the question `Consolidation` was built for.
 *
 *   baseCurrency  what the two amounts are stated in (GEN-107), read from the
 *                 currency table — the 'USD' literal this replaced was wrong
 *                 for every company that is not American.
 *   rateDate      GEN-115's date, and GEN-116's switch: the effective date of
 *                 the rates actually used, or NULL when nothing converted, in
 *                 which case the screen shows no disclosure at all.
 *   conversionNotes  ERR-DSH-04-04, one sentence per currency that had no
 *                 rate on or before today. Those projects' budgets are
 *                 EXCLUDED from the two totals and NAMED — never silently
 *                 added, which is F-27 and the defect this ends.
 *
 * `activeCount` and `avgProgressPct` count projects and average a percentage,
 * so an excluded project still appears in both. That is deliberate: it is
 * still a running project, and only its MONEY is unstateable.
 */
export const ProjectSummarySchema = z
    .object({
    activeCount: z.number().int().min(0),
    activeBudget: MoneyStringSchema,
    /** The derived spent figure, summed — never the stored defect. */
    spent: MoneyStringSchema,
    avgProgressPct: z.number().int().min(0).max(100),
    baseCurrency: z.string(),
    rateDate: IsoDateSchema.nullable(),
    conversionNotes: z.string().array(),
})
    .strict();
export const CreateProjectRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(160),
    /** A customer record — or omit it and set internal (BR-PRJ-01-10). */
    partnerId: z.uuid().optional(),
    isInternal: z.boolean().optional(),
    managerId: z.uuid(),
    startDate: IsoDateSchema,
    endDate: IsoDateSchema,
    budget: MoneyStringSchema,
    /** Pre-filled true; forced false on internal projects (server). */
    billable: z.boolean().optional(),
    billingType: BillingTypeSchema.optional(),
    billingFrequency: BillingFrequencySchema.optional(),
    flatRate: MoneyStringSchema.optional(),
    notes: z.string().trim().max(2000).optional(),
    /** PRJ-01/G-03: the site. */
    siteAddress: z.string().trim().min(1).max(300).optional(),
    siteCity: z.string().trim().min(1).max(120).optional(),
    siteGeo: SiteGeoSchema.optional(),
    /** PRJ-01: the whole team at creation (replace-all; the card is seeded first). */
    members: TeamMemberInputSchema.array().max(200).optional(),
})
    .strict();
export const UpdateProjectRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(160).optional(),
    managerId: z.uuid().optional(),
    status: ProjectStatusSchema.optional(),
    startDate: IsoDateSchema.optional(),
    endDate: IsoDateSchema.optional(),
    budget: MoneyStringSchema.optional(),
    billable: z.boolean().optional(),
    billingType: BillingTypeSchema.optional(),
    billingFrequency: BillingFrequencySchema.optional(),
    flatRate: MoneyStringSchema.optional(),
    notes: z.string().trim().max(2000).optional(),
    /** PRJ-01/G-03: the site — null clears a field. */
    siteAddress: z.string().trim().min(1).max(300).nullable().optional(),
    siteCity: z.string().trim().min(1).max(120).nullable().optional(),
    siteGeo: SiteGeoSchema.nullable().optional(),
    /** Present = REPLACE the whole team (members absent from the list leave). */
    members: TeamMemberInputSchema.array().max(200).optional(),
})
    .strict();
export const AddMemberRequestSchema = z
    .object({
    employeeId: z.uuid(),
    rateRole: z.string().trim().min(1).max(60).optional(),
    /** PRJ-01: the job on site. */
    role: z.string().trim().min(1).max(80).optional(),
})
    .strict();
export const SetRateCardRequestSchema = z
    .object({
    /** The whole configurable list (the hard-coded-three defect, fixed). */
    roles: z
        .object({
        roleName: z.string().trim().min(1).max(60),
        hourlyRate: MoneyStringSchema,
    })
        .strict()
        .array()
        .min(1),
})
    .strict();
export const CreateMilestoneRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(160),
    dueDate: IsoDateSchema,
    amount: MoneyStringSchema.optional(),
})
    .strict();
export const UpdateMilestoneRequestSchema = z
    .object({
    name: z.string().trim().min(1).max(160).optional(),
    dueDate: IsoDateSchema.optional(),
    amount: MoneyStringSchema.optional(),
    /** 'invoiced' is billing's alone (BR-PRJ-04-09) — not settable here. */
    status: z.enum(['not_started', 'in_progress', 'completed']).optional(),
})
    .strict();
/**
 * PRJ-01 field 4 / §11's assignable-people list: ACTIVE employees. `id` is
 * an employee id, and `code` is EMP-#### so two people with one name are
 * still tellable apart.
 */
export const PersonSchema = z
    .object({ id: z.uuid(), name: z.string(), code: z.string(), jobTitle: z.string() })
    .strict();
/**
 * The OTHER list, and the reason there are two: a task's assignee is an
 * ACCOUNT, because BR-PRJ-02-01 makes a project task a row in the one shared
 * task engine and "My Tasks" is a signed-in person's own queue. `name` shows
 * the staff name where the account is joined to an employee record, so the
 * two pickers read the same for the same person.
 */
export const ProjectAccountSchema = z.object({ id: z.uuid(), name: z.string() }).strict();
const projectParams = z.object({ projectId: z.uuid() }).strict();
const milestoneParams = z.object({ milestoneId: z.uuid() }).strict();
export const projectsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/projects',
        query: pagedQuery({
            status: ProjectStatusSchema.optional(),
            managerId: z.uuid().optional(),
            /** PRJ-01 §4's search box: the project name, or the code it prints. */
            q: z.string().trim().max(120).optional(),
            /**
             * GEN-50's second read — the project a form is ALREADY holding.
             * PRJ-02's board and PRJ-04's billing screen both resolve one by id,
             * and it ignores `status`: a CANCELLED project's approved hours stay
             * billable (BR-PRJ-01-09), so the billing screen must still name it.
             */
            ids: PickerIdsSchema.optional(),
        }),
        responses: {
            200: pagedResponseWith('projects', ProjectSchema, { summary: ProjectSummarySchema }),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'PRJ-01, PAGED (NFR-42) — every filter in SQL. Each row carries DERIVED spent (BR-PRJ-01-04) and progress (BR-PRJ-01-05). The four headline figures are BR-PRJ-01-02’s, over Planning/Active/On hold — beside the page, never from it.',
    },
    get: {
        method: 'GET',
        path: '/api/projects/:projectId',
        pathParams: projectParams,
        responses: { 200: ProjectDetailSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'One project with its team, rate card and the ONE milestone list (Q-PRJ-01-5).',
    },
    people: {
        method: 'GET',
        path: '/api/projects/people',
        responses: {
            200: z.object({ people: PersonSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The assignable-people list: ACTIVE employees (PRJ-01 field 4 "Pick from list (Employees)"), which is what makes every team avatar resolve — the PRJ-01 defect.',
    },
    accounts: {
        method: 'GET',
        path: '/api/projects/accounts',
        responses: {
            200: z.object({ accounts: ProjectAccountSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Who a TASK can be assigned to: accounts, because a project task is a row in the shared task engine and lands in that person\'s own My Tasks (BR-PRJ-02-01).',
    },
    create: {
        method: 'POST',
        path: '/api/projects',
        body: CreateProjectRequestSchema,
        responses: { 201: ProjectDetailSchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema, 409: MessageSchema },
        summary: 'BR-PRJ-01-08: PRJ-#### from the tenant series; client = customer record or internal (then non-billable automatically); currency from the customer (Q-PRJ-01-3); seeds the default rate card and the five task statuses.',
    },
    update: {
        method: 'PATCH',
        path: '/api/projects/:projectId',
        pathParams: projectParams,
        body: UpdateProjectRequestSchema,
        responses: { 200: ProjectDetailSchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'Edit a project + its PRJ-04 billing configuration; status moves follow §7 (completed/cancelled are ends). Internal stays non-billable.',
    },
    addMember: {
        method: 'POST',
        path: '/api/projects/:projectId/members',
        pathParams: projectParams,
        body: AddMemberRequestSchema,
        responses: { 201: ProjectDetailSchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'Add a team member with a price band (default Senior Consultant, Q-PRJ-04-2).',
    },
    updateMember: {
        method: 'PATCH',
        path: '/api/projects/:projectId/members/:memberId',
        pathParams: projectParams.extend({ memberId: z.uuid() }).strict(),
        body: z.object({ rateRole: z.string().trim().min(1).max(60) }).strict(),
        responses: { 200: ProjectDetailSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: "Change a member's price band — must exist on the rate table.",
    },
    removeMember: {
        method: 'DELETE',
        path: '/api/projects/:projectId/members/:memberId',
        pathParams: projectParams.extend({ memberId: z.uuid() }).strict(),
        responses: { 200: ProjectDetailSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'Remove a team member. Their logged hours remain — facts survive the roster.',
    },
    setRateCard: {
        method: 'PUT',
        path: '/api/projects/:projectId/rate-card',
        pathParams: projectParams,
        body: SetRateCardRequestSchema,
        responses: { 200: ProjectDetailSchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: "PRJ-04 §5a: replace the project's role rate table (configurable list). A role a member holds cannot be removed.",
    },
    addMilestone: {
        method: 'POST',
        path: '/api/projects/:projectId/milestones',
        pathParams: projectParams,
        body: CreateMilestoneRequestSchema,
        responses: { 201: ProjectDetailSchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema },
        summary: 'BR-PRJ-01-07: milestones are creatable — name, due date, amount.',
    },
    updateMilestone: {
        method: 'PATCH',
        path: '/api/projects/milestones/:milestoneId',
        pathParams: milestoneParams,
        body: UpdateMilestoneRequestSchema,
        responses: { 200: ProjectDetailSchema, 400: MessageSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: "BR-PRJ-01-07: editable and COMPLETABLE from the project screen. Invoiced is billing's alone and an invoiced milestone is frozen (BR-PRJ-04-09).",
    },
    removeMilestone: {
        method: 'DELETE',
        path: '/api/projects/milestones/:milestoneId',
        pathParams: milestoneParams,
        responses: { 200: ProjectDetailSchema, 401: MessageSchema, 403: MessageSchema, 404: MessageSchema, 409: MessageSchema },
        summary: 'Remove an un-invoiced milestone. An invoiced one is locked (trigger).',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=projects.js.map